import { useEffect, useState } from "react";
import { Search, Plus, Pencil, Trash2, UserX, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { getDoctors, deleteDoctor, setDoctorActive } from "../../api/api";
import DataTable from "../../components/DataTable";
import AddDoctorModal from "../../components/AddDoctorModal";
import DoctorDrawer from "../../components/DoctorDrawer";
import MarkDelayModal from "../../components/MarkDelayModal";
import GeneratedCredentialsModal from "../../components/GeneratedCredentialsModal";
import ConfirmModal from "../../components/ConfirmModal";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";

export default function Doctors() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [drawerDoctorId, setDrawerDoctorId] = useState(null);
  const [delayDoctor, setDelayDoctor] = useState(null);
  const [deletingDoctor, setDeletingDoctor] = useState(null);
  const [togglingDoctor, setTogglingDoctor] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [credentialsModal, setCredentialsModal] = useState({ open: false, credentials: [] });

  const load = () => {
    setLoading(true);
    getDoctors({ search, limit: 50, include_inactive: true })
      .then((res) => setDoctors(res.data))
      .catch((err) => toast.error(err?.message || "Could not load doctors."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, refreshToken]);

  const statusBadge = (doctor) => {
    if (doctor.active === false) return <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>;
    if (doctor.on_leave_today) return <Badge variant="destructive">On leave today</Badge>;
    if (doctor.delay) return <Badge variant="warning">Delayed +{doctor.delay.minutes}m</Badge>;
    return <Badge variant="secondary">On duty</Badge>;
  };

  const columns = [
    {
      key: "name",
      header: "Doctor",
      render: (d) => (
        <div className={d.active === false ? "opacity-60" : undefined}>
          <p className="font-medium">{d.name}</p>
          <p className="text-xs text-muted-foreground">
            {d.specialty} · {d.reg_no}
          </p>
        </div>
      ),
    },
    {
      key: "department",
      header: "Department",
      render: (d) => d.department_id?.name
        ? <Badge variant="outline">{d.department_id.name}</Badge>
        : <span className="text-xs text-muted-foreground">Unassigned</span>,
    },
    { key: "fee", header: "Fee", render: (d) => `₹${d.fee}` },
    { key: "phone", header: "Phone" },
    { key: "status", header: "Status", render: statusBadge },
    {
      key: "actions",
      header: "",
      render: (d) => (
        <div className="flex justify-end gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            aria-label={`Edit ${d.name}`}
            onClick={(e) => { e.stopPropagation(); setEditingDoctor(d); }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            aria-label={d.active === false ? `Mark ${d.name} active` : `Mark ${d.name} inactive`}
            title={d.active === false ? "Mark active" : "Mark inactive"}
            onClick={(e) => { e.stopPropagation(); setTogglingDoctor(d); }}
          >
            {d.active === false ? <UserCheck className="h-3.5 w-3.5" /> : <UserX className="h-3.5 w-3.5" />}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-destructive hover:text-destructive"
            aria-label={`Delete ${d.name}`}
            onClick={(e) => { e.stopPropagation(); setDeletingDoctor(d); }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Doctors</h2>
          <p className="text-sm text-muted-foreground">Manage your care team — availability, delays and leave</p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4" /> Add doctor
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search by name, specialty, or department…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <DataTable
        columns={columns}
        data={doctors}
        loading={loading}
        emptyMessage="No doctors found."
        onRowClick={(doctor) => setDrawerDoctorId(doctor._id)}
      />

      <AddDoctorModal open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
      <AddDoctorModal
        open={!!editingDoctor}
        onOpenChange={(v) => !v && setEditingDoctor(null)}
        doctor={editingDoctor}
        onCreated={load}
        onLoginCredentials={(cred) => setCredentialsModal({ open: true, credentials: [cred] })}
      />
      <GeneratedCredentialsModal
        open={credentialsModal.open}
        onOpenChange={(open) => setCredentialsModal((c) => ({ ...c, open }))}
        credentials={credentialsModal.credentials}
      />
      <ConfirmModal
        open={!!togglingDoctor}
        onOpenChange={(v) => !v && setTogglingDoctor(null)}
        title={togglingDoctor?.active === false ? `Mark ${togglingDoctor?.name} active?` : `Mark ${togglingDoctor?.name || "doctor"} inactive?`}
        description={
          togglingDoctor?.active === false
            ? "They'll be bookable again and their dashboard login will work again."
            : "They'll be hidden from booking, attendance and doctor lists, and their dashboard login is switched off. Past appointments, bills and prescriptions stay as they are. You can mark them active again any time."
        }
        confirmLabel={togglingDoctor?.active === false ? "Mark active" : "Mark inactive"}
        onConfirm={async () => {
          try {
            const res = await setDoctorActive(togglingDoctor._id, togglingDoctor.active === false);
            toast.success(res?.message || "Saved.");
            load();
          } catch (err) {
            toast.error(err?.message || "Could not update doctor.");
          }
        }}
      />
      <ConfirmModal
        open={!!deletingDoctor}
        onOpenChange={(v) => !v && setDeletingDoctor(null)}
        title={`Delete ${deletingDoctor?.name || "doctor"}?`}
        description="This removes the doctor and their dashboard login. Only possible for a doctor with no appointments, bills or prescriptions — use it for a duplicate or wrongly added doctor."
        confirmLabel="Delete doctor"
        onConfirm={async () => {
          try {
            await deleteDoctor(deletingDoctor._id);
            toast.success(`${deletingDoctor.name} deleted.`);
            load();
          } catch (err) {
            toast.error(err?.message || "Could not delete doctor.");
          }
        }}
      />
      <DoctorDrawer
        doctorId={drawerDoctorId}
        onClose={() => setDrawerDoctorId(null)}
        onMarkDelay={(doctor) => setDelayDoctor(doctor)}
        refreshToken={refreshToken}
      />
      <MarkDelayModal
        open={!!delayDoctor}
        onOpenChange={(v) => !v && setDelayDoctor(null)}
        doctor={delayDoctor}
        onSaved={() => {
          setRefreshToken((t) => t + 1);
          load();
        }}
      />
    </div>
  );
}
